import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  queryOptions,
  useMutation,
  useQueries,
  useQueryClient,
} from '@tanstack/react-query';
import { HTTPError, TimeoutError } from 'ky';
import { api } from 'lib/request/api';
import { subscribe } from 'lib/pusher';
import type {
  AcceptedUpload,
  GeometryUpload,
  GeometryUploadFile,
  GeometryUploadItem,
  UploadStatusRead,
  VerifiedUpload,
} from 'types/area-geometry';
import { combineUploads, uploadProblem } from 'utils/area-geometry-upload';

/** How often the status is read while the job runs; the Pusher event can beat it. */
const POLL_MS = 2_000;

/** A status read that fails is tried this many more times before the file reads as failed. */
const STATUS_RETRIES = 3;

/** A 25 MB file on a slow line takes longer than the usual 30 s. */
const UPLOAD_TIMEOUT_MS = 180_000;

const STATUS_EVENT = 'area_geometry_upload.status.changed';

/** A Pusher channel, as far as this hook uses it. */
interface UploadChannel {
  bind: (event: string, handler: () => void) => void;
  unbind: (event: string, handler: () => void) => void;
  unsubscribe: () => void;
}

/**
 * Whether the verify job has finished, one way or the other.
 *
 * @param {UploadStatusRead | undefined} read - The last status read.
 * @return {boolean} Whether there is nothing more to wait for.
 */
const isDone = (read: UploadStatusRead | undefined): boolean =>
  read?.status === 'completed' || read?.status === 'failed';

/**
 * A file's upload status, read until its job is done.
 *
 * @param {number | undefined} id - The upload, once the server accepted it.
 * @param {string} localId - Keys a file not yet accepted, so files being sent don't share a query.
 * @return {object} The query options.
 */
const uploadStatusQuery = (id: number | undefined, localId = '') =>
  queryOptions({
    queryKey: ['area-geometry-upload', id ?? localId],
    queryFn: ({ signal }) =>
      api
        .get(`area_geometry_uploads/${id}`, { signal })
        .json<UploadStatusRead>(),
    enabled: id !== undefined,
    // A blip mid-poll is tried again rather than failing the file; once the reads
    // keep failing, as a purged upload's 404 does, the polling stops.
    retry: STATUS_RETRIES,
    refetchInterval: (query) =>
      isDone(query.state.data) || query.state.status === 'error'
        ? false
        : POLL_MS,
    staleTime: Infinity,
  });

/**
 * Each file's last read and whether it failed. Kept at module scope so `useQueries`
 * keeps its result while no read changed.
 *
 * @param {Array} results - The status queries' results.
 * @return {Array} The reads.
 */
const statusReads = (
  results: { data: UploadStatusRead | undefined; isError: boolean }[]
) => results.map(({ data, isError }) => ({ data, isError }));

/**
 * Why the file could not be sent.
 *
 * @param {unknown} error - The thrown request error.
 * @return {string} What to show on the file's row.
 */
const sendFailure = (error: unknown): string => {
  if (error instanceof TimeoutError) {
    return 'The upload took too long. Try again.';
  }
  if (error instanceof HTTPError) {
    return `The file could not be uploaded (error ${error.response.status}). Try again.`;
  }
  return 'The server could not be reached. Try again.';
};

/**
 * Where a file stands, from what is known of it locally and its last status read.
 *
 * @param {GeometryUploadFile} file - The picked file.
 * @param {UploadStatusRead | undefined} read - The last status read.
 * @param {boolean} readFailed - Whether the status could not be read.
 * @return {GeometryUploadItem} The file with its state, result and refusal.
 */
const uploadItem = (
  file: GeometryUploadFile,
  read: UploadStatusRead | undefined,
  readFailed: boolean
): GeometryUploadItem => {
  const none = { file, result: null, error: null };
  if (file.error) return { ...none, state: 'failed', error: file.error };
  if (read?.status === 'completed') {
    const { verified, ignored, geojson } = read;
    return {
      ...none,
      state: 'verified',
      result: { verified, ignored, geojson },
    };
  }
  if (read?.status === 'failed') {
    return { ...none, state: 'failed', error: read.error };
  }
  if (readFailed) {
    return {
      ...none,
      state: 'failed',
      error: 'The file could not be checked. Try uploading it again.',
    };
  }
  return { ...none, state: file.id === undefined ? 'uploading' : 'verifying' };
};

/**
 * Reads a file's status now rather than at the next poll, whenever its job
 * announces a change. The read stays the source of truth; the event is a nudge.
 *
 * @param {string} watched - "id:channel" for each file still being checked, comma-joined.
 */
function useUploadEvents(watched: string): void {
  const queryClient = useQueryClient();
  // Each file keeps its subscription while it is checked, so one file finishing
  // never drops and re-opens the others' channels, where an event could slip by.
  const subscriptions = useRef(new Map<string, () => void>());
  useEffect(() => {
    const entries = new Set(watched ? watched.split(',') : []);
    subscriptions.current.forEach((unsubscribe, entry) => {
      if (entries.has(entry)) return;
      unsubscribe();
      subscriptions.current.delete(entry);
    });
    entries.forEach((entry) => {
      if (subscriptions.current.has(entry)) return;
      const [id, channel] = entry.split(':');
      const subscription = subscribe(channel) as UploadChannel;
      /** Reads this file's status now. */
      const refresh = () =>
        void queryClient.invalidateQueries({
          queryKey: uploadStatusQuery(Number(id)).queryKey,
        });
      subscription.bind(STATUS_EVENT, refresh);
      subscriptions.current.set(entry, () => {
        subscription.unbind(STATUS_EVENT, refresh);
        subscription.unsubscribe();
      });
    });
  }, [watched, queryClient]);
  useEffect(() => {
    const open = subscriptions.current;
    return () => {
      open.forEach((unsubscribe) => unsubscribe());
      open.clear();
    };
  }, []);
}

/**
 * Story 4's upload of boundary files. Each file goes to the server, which keeps
 * it and checks it on a background job; the job's Pusher event says when to read
 * the result, and a poll covers a missed event. Once every file is done, the
 * verified files' polygons come back as one shape, to draw and to save; a file
 * that fails is shown with its reason and left out.
 *
 * @return {GeometryUpload} The files, where each stands, and the actions on them.
 */
export function useGeometryUpload(): GeometryUpload {
  const [files, setFiles] = useState<GeometryUploadFile[]>([]);
  // A response for a file since removed is dropped.
  const current = useRef<GeometryUploadFile[]>([]);
  // What the server said of each file sent, kept even once the file is off the
  // list: an undo can bring back a file as it stood before its answer came.
  const answers = useRef(new Map<string, Partial<GeometryUploadFile>>());

  const { mutateAsync } = useMutation({
    mutationFn: (picked: File) => {
      const body = new FormData();
      body.append('file', picked);
      return api
        .post('area_geometry_uploads', { body, timeout: UPLOAD_TIMEOUT_MS })
        .json<AcceptedUpload>();
    },
    retry: false,
  });

  const set = useCallback((next: GeometryUploadFile[]) => {
    // Clearing nothing keeps the same list, so a survey-row edit or a close that
    // clears the files doesn't re-render everything keyed on them.
    if (next.length === 0 && current.current.length === 0) return;
    const known = next.map((file) => {
      const answer = answers.current.get(file.localId);
      return answer ? { ...file, ...answer } : file;
    });
    current.current = known;
    setFiles(known);
  }, []);

  const remove = useCallback(
    (localId: string) =>
      set(current.current.filter((file) => file.localId !== localId)),
    [set]
  );

  /** Sends one file and records what the server said, if the file is still picked. */
  const sendOne = useCallback(
    async (picked: File, localId: string) => {
      let patch: Partial<GeometryUploadFile>;
      try {
        const accepted = await mutateAsync(picked);
        // A file-level refusal (type, size) comes back as a 201 with `error`.
        patch =
          'id' in accepted
            ? {
                id: accepted.id,
                channel: accepted.channel,
                fileSignedId: accepted.fileSignedId,
              }
            : { error: accepted.error };
      } catch (error) {
        patch = { error: sendFailure(error) };
      }
      answers.current.set(localId, patch);
      if (!current.current.some((file) => file.localId === localId)) return;
      set(
        current.current.map((file) =>
          file.localId === localId ? { ...file, ...patch } : file
        )
      );
    },
    [mutateAsync, set]
  );

  const add = useCallback(
    (picked: File[]) => {
      const added = picked.map((file) => {
        const problem = uploadProblem(file);
        return {
          localId: crypto.randomUUID(),
          name: file.name,
          error: problem ?? undefined,
        };
      });
      set([...current.current, ...added]);
      added.forEach(({ localId, error }, index) => {
        if (!error) void sendOne(picked[index], localId);
      });
    },
    [sendOne, set]
  );

  // `combine` keeps the reads' identity while none changed, so the joined shape
  // below does too, and the preview built from it isn't asked for again.
  const reads = useQueries({
    queries: files.map((file) => uploadStatusQuery(file.id, file.localId)),
    combine: statusReads,
  });

  useUploadEvents(
    files
      .filter((file, index) => file.channel && !isDone(reads[index]?.data))
      .map((file) => `${file.id}:${file.channel}`)
      .join(',')
  );

  const view = useMemo(() => {
    const items = files.map((file, index) =>
      uploadItem(file, reads[index]?.data, reads[index]?.isError ?? false)
    );
    const busy = items.some(
      ({ state }) => state === 'uploading' || state === 'verifying'
    );
    const verified = items.filter(({ state }) => state === 'verified');
    const result = busy
      ? null
      : combineUploads(
          verified.flatMap(({ result: one }): VerifiedUpload[] =>
            one ? [one] : []
          )
        );
    const fileSignedIds = verified.flatMap(({ file }) =>
      file.fileSignedId ? [file.fileSignedId] : []
    );
    return { items, busy, result, fileSignedIds };
  }, [files, reads]);

  return { ...view, files, add, remove, set };
}
